import { useEffect, useMemo, useState } from 'react';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { QButton, QModal, QModalActions, QModalContent, QTextField, QTypography } from '@kw/quokka-ui';
import { CountryFlag } from '../../CountryFlag';
import { CountryOption } from './countrySettings';

interface CountryPickerModalProps {
  id: string;
  title: string;
  open: boolean;
  options: CountryOption[];
  multiple?: boolean;
  selectedValue?: string | null;
  selectedValues?: string[];
  onSelect?: (countryCode: string) => void;
  onSelectionChange?: (countryCodes: string[]) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
  saveDisabled: boolean;
  errorMessage?: string;
  contentHeight?: string;
  listTestId?: string;
}

const NO_SELECTED_COUNTRIES: string[] = [];

export const CountryPickerModal = ({
  id,
  title,
  open,
  options,
  multiple = false,
  selectedValue,
  selectedValues = NO_SELECTED_COUNTRIES,
  onSelect,
  onSelectionChange,
  onClose,
  onSave,
  isSaving,
  saveDisabled,
  errorMessage,
  contentHeight = '560px',
  listTestId,
}: CountryPickerModalProps) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return options;

    return options.filter(option => option.label.toLocaleLowerCase().includes(normalizedQuery));
  }, [options, query]);

  const selectedCountryCodes = useMemo(
    () => (multiple ? selectedValues : selectedValue ? [selectedValue] : []),
    [multiple, selectedValue, selectedValues],
  );
  const selectedCountryCodeSet = useMemo(() => new Set(selectedCountryCodes), [selectedCountryCodes]);
  const selectedOptions = useMemo(
    () => selectedCountryCodes.map(countryCode => options.find(option => option.value === countryCode)).filter(Boolean),
    [options, selectedCountryCodes],
  );

  // QModal leaves its outer Paper in the document when closed. Rendering nothing
  // prevents that empty element from becoming an extra item in card layouts.
  if (!open) return null;

  const selectCountry = (countryCode: string) => {
    if (!multiple) {
      onSelect?.(countryCode);
      return;
    }

    const nextSelectedValues = selectedCountryCodeSet.has(countryCode)
      ? selectedValues.filter(value => value !== countryCode)
      : [...selectedValues, countryCode];
    onSelectionChange?.(nextSelectedValues);
  };

  const titleId = `${id}-title`;
  const listId = `${id}-list`;

  return (
    <QModal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth={false}
      PaperProps={pickerPaperProps}
      title={
        <PickerHeader>
          <PickerTitle id={titleId} variant="h3Medium">
            {title}
          </PickerTitle>
          <SearchField
            autoFocus
            fullWidth
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search countries..."
            inputProps={{
              'aria-label': 'Search countries',
              'aria-controls': listId,
              'aria-expanded': true,
              role: 'combobox',
            }}
          />
          {multiple && selectedOptions.length > 0 && (
            <SelectedCountries role="list" aria-label="Selected countries">
              {selectedOptions.map(option => (
                <SelectedCountry key={option.value} role="listitem">
                  <CountryFlag countryCode={option.value} />
                  <SelectedCountryCode>{option.value}</SelectedCountryCode>
                  <RemoveSelectionButton
                    type="button"
                    aria-label={`Remove ${option.label} selection`}
                    onClick={() => selectCountry(option.value)}
                  >
                    <Close />
                  </RemoveSelectionButton>
                </SelectedCountry>
              ))}
            </SelectedCountries>
          )}
        </PickerHeader>
      }
      content={
        <PickerContent contentHeight={contentHeight}>
          <CountryList
            id={listId}
            data-testid={listTestId}
            role="listbox"
            aria-label="Countries"
            aria-multiselectable={multiple || undefined}
          >
            {filteredOptions.map(option => {
              const isDisabled = Boolean(option.disabledReason);
              const isSelected = selectedCountryCodeSet.has(option.value);

              return (
                <li key={option.value}>
                  <CountryOptionButton
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    data-selected={isSelected}
                    onClick={() => selectCountry(option.value)}
                  >
                    <FlagContainer>
                      <CountryFlag countryCode={option.value} />
                    </FlagContainer>
                    <CountryName>{option.label}</CountryName>
                    {option.disabledReason && <BlockedCountryLabel>{option.disabledReason}</BlockedCountryLabel>}
                  </CountryOptionButton>
                </li>
              );
            })}
            {!filteredOptions.length && <NoCountriesFound>No countries found.</NoCountriesFound>}
          </CountryList>
          {errorMessage && <PickerError role="alert">{errorMessage}</PickerError>}
        </PickerContent>
      }
      actions={
        <PickerActions>
          <QButton variant="outlined" onClick={onClose} disabled={isSaving}>
            Cancel
          </QButton>
          <QButton variant="filled" onClick={onSave} disabled={saveDisabled}>
            {isSaving ? 'Saving…' : 'Save'}
          </QButton>
        </PickerActions>
      }
    />
  );
};

const pickerPaperProps = {
  sx: {
    width: '420px',
    minWidth: 0,
    maxWidth: 'calc(100vw - 48px)',
    maxHeight: 'calc(100vh - 24px)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
};

const PickerHeader = styled('div')`
  padding: 16px 18px 12px;
`;

const PickerTitle = styled(QTypography)`
  display: block;
  font-size: 15px;
  line-height: 21px;
  font-weight: 700;
`;

const SearchField = styled(QTextField)`
  margin-top: 12px;

  .MuiInputBase-root {
    min-height: 40px;
    border-radius: 8px;
  }
`;

const SelectedCountries = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const SelectedCountry = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 8px;
  border: 1px solid #d7dfe2;
  border-radius: 999px;
  background: #f7f9fa;

  img {
    width: 18px !important;
    height: 14px !important;
  }
`;

const SelectedCountryCode = styled('span')`
  color: #33474e;
  font-size: 11.5px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.03em;
`;

const RemoveSelectionButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #66777d;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #e2e8ea;
    color: #10252d;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

const PickerContent = styled(QModalContent, {
  shouldForwardProp: propertyName => propertyName !== 'contentHeight',
})<{ contentHeight: string }>`
  height: min(${({ contentHeight }) => contentHeight}, calc(100vh - 190px));
  min-height: 280px;
  padding: 6px;
`;

const CountryList = styled('ul')`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const CountryOptionButton = styled('button')`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 8px 12px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #10252d;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible,
  &[data-selected='true'] {
    background: #eef5f5;
  }

  &[data-selected='true'] {
    font-weight: 700;
  }

  &:disabled {
    color: #7d898d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FlagContainer = styled('span')`
  display: inline-flex;
  justify-content: center;
  width: 24px;
  flex: 0 0 24px;
`;

const CountryName = styled('span')`
  min-width: 0;
`;

const BlockedCountryLabel = styled('span')`
  margin-left: auto;
  color: #7d898d;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

const NoCountriesFound = styled('li')`
  padding: 18px 12px;
  color: #66777d;
  font-size: 14px;
  line-height: 20px;
`;

const PickerError = styled('div')`
  margin-top: 8px;
  color: #df2e21;
  font-size: 13px;
  line-height: 18px;
`;

const PickerActions = styled(QModalActions)`
  gap: 10px;
  padding: 13px 18px 14px;
  border-top: 1px solid #d7dfe2;

  button {
    min-width: 74px;
  }
`;
